// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ɵEmptyOutletComponent } from '@angular/router';
import { Observable, Subscription, combineLatest, map } from 'rxjs';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import { Observe } from 'src/app/modules/shared/utils/as-observable';
import { SystemDetailsModel } from '../../../../api/remote-api/.portal.generated/models';
import { PatternMatchDataService } from '../../../patternmatch/services/pattern-match-data/pattern-match-data.service';
import { PortalWebAppConfigurationService } from '../../services/portal-web-app-configuration.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights-service/application-insights.service';

@Component({
  selector: 'app-useful-applications',
  templateUrl: './useful-applications.component.html',
  styleUrls: ['./useful-applications.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgTemplateOutlet,
    ɵEmptyOutletComponent,
    AsyncPipe,
    NgStyle,
    MatTooltipModule,
  ],
})
export class UsefulApplications implements OnInit {
  @Input({ required: true }) menuMode = true;
  @Input({ required: false }) showTitle = true;
  @Input() allowSystemContext = true;
  @Input() displayType = 'grid';
  @Input() flexWrapType = 'nowrap';
  @Input() showInfoIcon = true;

  @Observe('allowSystemContext')
  private allowSystemContext$!: Observable<typeof this.allowSystemContext>;

  configurationController = inject(PortalWebAppConfigurationService);
  protected systemOverviewService = inject(SystemOverviewService);
  private settingsController = inject(SettingsService);
  private patternMatchDataService = inject(PatternMatchDataService);
  screenLayoutService = inject(ScreenLayoutService);
  private applicationInsightsService = inject(ApplicationInsightsService);

  showFeedback = false;
  onSystemDetailsLoadedSubscriber: Subscription | undefined;
  protected systemDetails: SystemDetailsModel | undefined;
  private currentModalityName: string | undefined;

  ngOnInit(): void {
    this.systemDetails$.subscribe((details) => {
      this.systemDetails = details;
    });
    this.systemOverviewService.currentModalityName$.subscribe((name) => {
      this.currentModalityName = this.convertModalityName(name);
    });
    // Share mrqDashboardUrl$ with PatternMatchDataService
    this.configurationController.usefulAppConfiguration$.subscribe((config) => {
      for (const app of config.applications ?? []) {
        if (app.title?.includes('MRQ Dashboard')) {
          const url = this.updatePlaceHolder(
            app.link ?? '',
            this.systemDetails,
          );
          this.patternMatchDataService.updateMrqDashboardUrl(url);
        }
      }
    });
  }

  systemDetails$ = combineLatest({
    systemDetails: this.systemOverviewService.systemDetails.data$,
    allowSystemContext: this.allowSystemContext$,
  }).pipe(
    map(({ allowSystemContext, systemDetails }) => {
      return allowSystemContext ? systemDetails : undefined;
    }),
  );

  convertModalityName(modalityName: string | undefined): string | undefined {
    switch (modalityName) {
      case 'D&A':
        return 'HS';
      case 'MI PET':
      case 'MI SPECT':
        return 'NM';
      case 'XP RF':
      case 'XP SU':
      case 'XP UR':
      case 'XP WH':
        return 'XP';
      default:
        return modalityName;
    }
  }

  updatePlaceHolder(url: string, systemDetails?: SystemDetailsModel): string {
    let modalityName;
    this.systemOverviewService.currentModalityName$.subscribe(
      (name) => (modalityName = this.convertModalityName(name)),
    );
    let dateFormat;
    this.settingsController.dateFormat$.subscribe(
      (format) => (dateFormat = format.longFormat),
    );
    if (url && systemDetails) {
      url = url.replaceAll(
        /\{systemSerialNumber\}/g,
        systemDetails.systemSerialNumber,
      );
      url = url.replaceAll(
        /\{systemMaterialNumber\}/g,
        String(systemDetails.systemMaterialNumber),
      );
      url = url.replaceAll(/\{systemModality\}/g, modalityName ?? '');
      url = url.replaceAll(/\{dateFormat\}/g, dateFormat ?? '');
    }
    return url ?? '';
  }

  onUsefulApplicationClick(label: string, url: string): void {
    if (this.systemDetails) {
      // User is inside a modality - track all system details
      this.applicationInsightsService.trackUsefulApplicationEvent(
        'UsefulApplicationClicked',
        {
          label: label,
          url: url,
          modalityName: this.currentModalityName ?? '',
          serialNumber: this.systemDetails.systemSerialNumber,
          materialNumber: this.systemDetails.systemMaterialNumber,
          productMarketingName: this.systemDetails.systemName,
        },
      );
    } else {
      // User is on home page - only track label and url, no modality details
      this.applicationInsightsService.trackUsefulApplicationEvent(
        'UsefulApplicationClicked',
        {
          label: label,
          url: url,
        },
      );
    }
  }
}

/**
 * replaces systemSerialNumber and systemMaterialNumber in case systemDetails are given
 */
export function processPlaceholders(
  url: string,
  systemDetails?: SystemDetailsModel,
): string {
  if (systemDetails) {
    url = url.replace(
      /\{systemSerialNumber\}/g,
      systemDetails.systemSerialNumber,
    );
    url = url.replace(
      /\{systemMaterialNumber\}/g,
      String(systemDetails.systemMaterialNumber),
    );
  }
  return url;
}
